import { GraphQLEnumType } from "graphql";
import type dinero from "dinero.js";

const values = {
  USD: { value: 1 },
  EUR: { value: 2 },
  JPY: { value: 3 },
  GBP: { value: 4 },
  AUD: { value: 5 },
  CAD: { value: 6 },
  CHF: { value: 7 },
  CNY: { value: 8 },
  SEK: { value: 9 },
  NZD: { value: 10 },
  MXN: { value: 11 },
  SGD: { value: 12 },
  HKD: { value: 13 },
  NOK: { value: 14 },
  KRW: { value: 15 },
  TRY: { value: 16 },
  RUB: { value: 17 },
  INR: { value: 18 },
  BRL: { value: 19 },
  ZAR: { value: 20 },
  DKK: { value: 21 },
  COP: { value: 22 },
  VND: { value: 23 },
  TWD: { value: 24 },
  PLN: { value: 25 },
  CZK: { value: 26 },
  RON: { value: 27 },
  UYU: { value: 28 },
  PEN: { value: 29 },
  UAH: { value: 30 },
  ARS: { value: 31 },
  CLP: { value: 32 },
  AED: { value: 33 },
  THB: { value: 34 },
};

export const CurrencyEnum = new GraphQLEnumType({
  name: "CurrencyEnum",
  values,
});

const CURRENCY_CODE_TO_NAME_MAP: Record<CurrencyCodeEnum, string> = {
  USD: "U.S. Dollar",
  EUR: "Euro",
  JPY: "Japanese Yen",
  GBP: "British Pound",
  AUD: "Australian Dollar",
  CAD: "Canadian Dollar",
  CHF: "Swiss Franc",
  CNY: "Chinese Yuan Renminbi",
  SEK: "Swedish Krona",
  NZD: "New Zealand Dollar",
  MXN: "Mexican Peso",
  SGD: "Singapore Dollar",
  HKD: "Hong Kong Dollar",
  NOK: "Norwegian Krone",
  KRW: "South Korean Won",
  TRY: "Turkish Lira",
  RUB: "Russian Ruble",
  INR: "Indian Rupee",
  BRL: "Brazilian Real",
  ZAR: "South African Rand",
  DKK: "Danish Krone",
  COP: "Colombian Peso",
  VND: "Vietnamese Dong",
  TWD: "Taiwan New Dollar",
  PLN: "Polish Złoty",
  CZK: "Czech Koruna",
  RON: "Romanian Leu",
  UYU: "Uruguayan Peso",
  PEN: "Peruvian Sol",
  UAH: "Ukrainian Hryvnia",
  ARS: "Argentine Peso",
  CLP: "Chilean Peso",
  AED: "Emirati Dirham",
  THB: "Thai Bhat",
};

export enum CurrencyCodeEnum {
  USD = "USD",
  EUR = "EUR",
  JPY = "JPY",
  GBP = "GBP",
  AUD = "AUD",
  CAD = "CAD",
  CHF = "CHF",
  CNY = "CNY",
  SEK = "SEK",
  NZD = "NZD",
  MXN = "MXN",
  SGD = "SGD",
  HKD = "HKD",
  NOK = "NOK",
  KRW = "KRW",
  TRY = "TRY",
  RUB = "RUB",
  INR = "INR",
  BRL = "BRL",
  ZAR = "ZAR",
  DKK = "DKK",
  COP = "COP",
  VND = "VND",
  TWD = "TWD",
  PLN = "PLN",
  CZK = "CZK",
  RON = "RON",
  UYU = "UYU",
  PEN = "PEN",
  UAH = "UAH",
  ARS = "ARS",
  CLP = "CLP",
  AED = "AED",
  THB = "THB",
}

export const symbols = Object.freeze({
  AUD: "$",
  BRL: "R$",
  CAD: "$",
  CHF: "CHF",
  CNY: "¥",
  EUR: "€",
  GBP: "£",
  HKD: "$",
  INR: "₹",
  JPY: "¥",
  KRW: "₩",
  MXN: "$",
  NOK: "kr",
  NZD: "$",
  SEK: "kr",
  SGD: "$",
  TRY: "₺",
  RUB: "₽",
  USD: "$",
  ZAR: "R",
  DKK: "kr.",
  COP: "$",
  VND: "₫",
  TWD: "$",
  PLN: "zł",
  CZK: "Kč",
  RON: "lei",
  UYU: "$",
  PEN: "S/",
  UAH: "₴",
  ARS: "$",
  CLP: "$",
  AED: "$",
  THB: "฿",
});

export type CurrencyCodeType = keyof typeof CurrencyCodeEnum;

/**
 * fetches the name of the currency (i.e "Euro") given the currency code ("eur")
 * @param currencyCode
 */
export function getCurrencyName(currencyCode: CurrencyCodeType): string {
  return CURRENCY_CODE_TO_NAME_MAP[currencyCode];
}

export function parseDineroCurrency(currency: dinero.Currency): CurrencyCodeType {
  if (currency.toString() in CurrencyCodeEnum) {
    return currency as CurrencyCodeType;
  }
  throw new Error(
    `Invalid currency code. Must be one of ${Object.values(CurrencyCodeEnum).join(", ")}`,
  );
}

export const isValidCurrencyValue = (currency: number | null | undefined): boolean => {
  if (currency === null || currency === undefined) {
    return false;
  }
  return CurrencyEnum.getValues().some((c) => c.value === currency);
};

export type Currency = {
  value: (typeof values)[keyof typeof values]["value"];
  name: (typeof CURRENCY_CODE_TO_NAME_MAP)[keyof typeof CURRENCY_CODE_TO_NAME_MAP];
  symbol: (typeof symbols)[keyof typeof symbols];
};
export const currencies: Currency[] = Object.keys(values).map((code) => ({
  value: values[code as keyof typeof values].value,
  name: CURRENCY_CODE_TO_NAME_MAP[code as keyof typeof CURRENCY_CODE_TO_NAME_MAP],
  symbol: symbols[code as keyof typeof symbols],
}));

export const getSortPriority = (currencyName: string) => {
  if (currencyName === CURRENCY_CODE_TO_NAME_MAP.USD) {
    return 1;
  }
  if (currencyName === CURRENCY_CODE_TO_NAME_MAP.EUR) {
    return 2;
  }
  if (currencyName === CURRENCY_CODE_TO_NAME_MAP.CAD) {
    return 3;
  }
  return 4;
};

function currencySorter(a: Currency, b: Currency) {
  /* Custom sort to place commonly used currencies at the top, with the rest sorted
   * alphabetically.
   * Manual sort order as requested in https://app.asana.com/0/1171261070025530/1174804031185890 */
  const aPriority = getSortPriority(a.name);
  const bPriority = getSortPriority(b.name);
  if (aPriority === bPriority) {
    const aFullname = a.name.toUpperCase();
    const bFullname = b.name.toUpperCase();
    if (!aFullname) {
      return -1;
    }
    if (!bFullname) {
      return 1;
    }
    if (aFullname < bFullname) {
      return -1;
    }
    if (aFullname > bFullname) {
      return 1;
    }
    return 0;
  }

  return aPriority - bPriority;
}

export const getSortedCurrencies = (): Currency[] => {
  return currencies.sort(currencySorter);
};
